<template>
  <div class="acc-bonus-program">
    <breadcrumbs :breadcrumbsData="breadcrumbsData" #icon>
      <svg class="icon" aria-hidden="true" style="color: #000">
        <use xlink:href="#icon-lianmengyingxiao"></use>
      </svg>
    </breadcrumbs>

    <div class="table">
      <div class="table-head">
        <a-space>
          <ButtonGroups :pagination="pagination" :tableData="dataSource" @pageChange="onPageChange" />
        </a-space>

        <a-space class="filters">
          <a-input-search v-model:value="keyword" :placeholder="t('seller.accBonusProgram.searchBrandPlaceholder')"
            allow-clear @search="onSearch" @pressEnter="onSearch" />
          <div class="statusFilter">
            <span class="filter-label">{{ t('seller.common.status') }}</span>
            <artmisads-select
              v-model:value="status"
              style="width: 160px"
              popup-class-name="acc-bonus-program-status-dropdown"
              :get-popup-container="getSelectPopupContainer"
              @change="onSearch"
            >
              <a-select-option value="">{{ t('seller.common.all') }}</a-select-option>
              <a-select-option value="INCOMING">{{
                t('seller.accBonusProgram.statusIncoming')
              }}</a-select-option>
              <a-select-option value="ACTIVE">{{
                t('seller.accBonusProgram.statusActive')
              }}</a-select-option>
              <a-select-option value="CANCELLED">{{
                t('seller.accBonusProgram.statusCancelled')
              }}</a-select-option>
              <a-select-option value="COMPLETED">{{
                t('seller.accBonusProgram.statusCompleted')
              }}</a-select-option>
            </artmisads-select>
          </div>
          <artmisads-button type="primary" @click="onAddProgram">
            {{ t('seller.accBonusProgram.addNewProgram') }}
          </artmisads-button>
        </a-space>
      </div>

      <artmisads-table v-model:pagination="pagination" :columns="columns" :data-source="dataSource"
        :change="onTableChange" :loading="load" :scroll="{ x: 1650 }">
        <template #headerCell="{ column }">
          <template v-if="column.key === 'sales'">
            <span>{{ column.title }}</span>
            <a-tooltip :title="t('seller.accBonusProgram.salesTooltip')">
              <Icon name="wenhao-tianchong" class="sales-tip-icon" />
            </a-tooltip>
          </template>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'campaignCount'">
            <a class="linkStyle" href="#" @click.prevent="openCampaigns(record)">
              {{ record.campaignCount }}
            </a>
          </template>
          <template v-if="column.key === 'bonusRuleSummary'">
            <!-- {{ record.bonusRuleSummary }} -->
            <div v-if="record.bonusRuleType === 'fixed'">
              {{ record.bonusRuleSummary }}
            </div>
            <div v-else>
              <a class="linkStyle" href="#" @click.prevent="openTierRates(record)">
                {{ t('seller.accBonusProgram.tierRates') }}
              </a>
            </div>
          </template>


          <template v-if="column.key === 'sales' || column.key === 'bonus'">
            {{ currencySymbol(record) }} {{ formatMoney(record[column.key]) }}
          </template>

          <template v-if="column.key === 'period'">
            <div>{{ t('seller.accBonusProgram.periodStart') }}: {{ formatPeriodDate(record.startDateInEpochMills) }}</div>
            <div v-if="record.cancelEffectiveDateInEpochMills ">
              {{ t('seller.accBonusProgram.periodCancel') }}: {{ formatPeriodDate(record.cancelEffectiveDateInEpochMills) }}
            </div>
            <div v-else>
              {{ t('seller.accBonusProgram.periodEnd') }}: {{ formatPeriodDate(record.endDateInEpochMills) }}
            </div>
          </template>

          <template v-if="column.key === 'publisherCount'">
            <a class="linkStyle" href="#" @click.prevent="openPublishers(record)">
              {{ record.publisherCount }}
            </a>
          </template>

          <template v-if="column.key === 'status'">
            <Status :type="statusType(record.status)" :text="record.status" :noBorder="true" />
          </template>

          <template v-if="column.key === 'action'">
            <span class="action-links">
              <template v-if="record.status === 'INCOMING'">
                <artmisads-select-button size="small" @click="onEdit(record)">
                  {{ t('seller.accBonusProgram.actionEdit') }}
                </artmisads-select-button>
                <artmisads-select-button size="small" @click="onView(record)">
                  {{ t('seller.accBonusProgram.actionView') }}
                </artmisads-select-button>
                <artmisads-select-button size="small" @click="onCancel(record)">
                  {{ t('seller.accBonusProgram.actionCancel') }}
                </artmisads-select-button>
              </template>
              <template v-else-if="record.status === 'CANCELLED' || record.status === 'COMPLETED'">
                <artmisads-select-button size="small" @click="onView(record)">
                  {{ t('seller.accBonusProgram.actionView') }}
                </artmisads-select-button>
              </template>
              <template v-else-if="record.status === 'ACTIVE'">
                <artmisads-select-button size="small" @click="onView(record)">
                  {{ t('seller.accBonusProgram.actionView') }}
                </artmisads-select-button>
                <artmisads-select-button size="small" @click="onCancel(record)">
                  {{ t('seller.accBonusProgram.actionCancel') }}
                </artmisads-select-button>
              </template>
            </span>
          </template>
        </template>
      </artmisads-table>
    </div>

    <CampaignsModal ref="campaignsModalRef" />
    <TierRatesModal ref="tierRatesModalRef" />
    <PublishersModal ref="publishersModalRef" />
    <CancelProgramModal ref="cancelModalRef" @success="getProgramList" />
    <AccAuthNoticeModal ref="accAuthNoticeModalRef" />
  </div>
</template>

<script setup>
import Status from "~/components/Status.vue"
import { useRouter } from 'vue-router'
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import moment from 'moment'
import { message } from 'ant-design-vue'
import breadcrumbs from '../../breadcrumbs.vue'
import ButtonGroups from '../../Brands/components/buttonGroups.vue'
import Icon from '@/components/Icon.vue'
import { accBonusProgramApi } from '@/server/index'

import CampaignsModal from './components/campaignsModal.vue'
import TierRatesModal from './components/tierRatesModal.vue'
import PublishersModal from './components/publishersModal.vue'
import CancelProgramModal from './components/cancelProgramModal.vue'
import AccAuthNoticeModal from './components/accAuthNoticeModal.vue'
import { CURRENCY_MAPPER, CURRENCY_DATA_MAPPER } from "@/contants/common"
import { useUserInfoStore } from "@/storeData"
const user = useUserInfoStore()
const countryCode = user?.userInfo?.countryCode || 'US'
const { t } = useI18n()
const router = useRouter()  
const load = ref(false)
const keyword = ref('')
const status = ref('')
const dataSource = ref([])

const getSelectPopupContainer = () => document.body

const campaignsModalRef = ref(null)
const tierRatesModalRef = ref(null)
const publishersModalRef = ref(null)
const cancelModalRef = ref(null)
const accAuthNoticeModalRef = ref(null)

const breadcrumbsData = computed(() => ({
  title: t('seller.accBonusProgram.pageTitle'),
  des: t('seller.accBonusProgram.pageDes'),
  hasTitleIcon: true,
  links: [
    { name: t('seller.common.home'), path: '/seller/dashboard' },
    {
      name: t('seller.menu.creatorConnections'),
      path: '/seller/creatorConnections/accBonusProgram',
    },
    {
      name: t('seller.accBonusProgram.pageTitle'),
      path: '/seller/creatorConnections/accBonusProgram',
    },
  ],
}))

const pagination = reactive({
  total: 0,
  current: 1,
  size: 10,
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50', '100'],
})

const columns = computed(() => [
  { title: t('seller.accBonusProgram.colTitle'), dataIndex: 'title', key: 'title', width: 140 },
  { title: t('seller.common.brand'), dataIndex: 'brandName', key: 'brandName', width: 120 },
  {
    title: t('seller.accBonusProgram.colCampaigns'),
    dataIndex: 'campaignCount',
    key: 'campaignCount',
    width: 100,
  },
  { title: t('seller.accBonusProgram.colBonusRule'), dataIndex: 'bonusRuleSummary', key: 'bonusRuleSummary', width: 150 },
  { title: t('seller.sellerAccount.colSales'), dataIndex: 'sales', key: 'sales', width: 110 },
  { title: t('seller.accBonusProgram.colBonus'), dataIndex: 'bonus', key: 'bonus', width: 100 },
  { title: t('seller.accBonusProgram.colPeriod'), dataIndex: 'period', key: 'period', width: 160 },
  {
    title: t('seller.accBonusProgram.colPublishers'),
    dataIndex: 'publisherCount',
    key: 'publisherCount',
    width: 100,
  },
  { title: t('seller.common.status'), dataIndex: 'status', key: 'status', width: 100 },
  { title: t('seller.accBonusProgram.colAction'), dataIndex: 'action', key: 'action', width: 180, fixed: 'right' },
])

const currencySymbol = (record) => CURRENCY_DATA_MAPPER[record.currency] || '$'
const formatMoney = (val) => Number(val || 0).toFixed(2)
const formatPeriodDate = (d) => (d ? moment(d).format('YYYY.M.D') : '--')

const statusLabel = (s) => {
  const map = {
    INCOMING: t('seller.accBonusProgram.statusIncoming'),
    ACTIVE: t('seller.accBonusProgram.statusActive'),
    CANCELLED: t('seller.accBonusProgram.statusCancelled'),
    COMPLETED: t('seller.accBonusProgram.statusCompleted'),
  }
  return map[s] || s
}



const getProgramList = async () => {
  load.value = true
  try {
    const res = await accBonusProgramApi.getProgramList({
      page: pagination.current,
      size: pagination.size,
      keyword: keyword.value,
      status: status.value,
      marketplace: CURRENCY_MAPPER[countryCode].host
      // marketplace:
    })
    if (res?.success && res?.data) {
      dataSource.value = res.data.data ?? []
      pagination.total = res.data.total ?? dataSource.value.length
    }
  } catch {
    
  } finally {
    load.value = false
  }
}

const onTableChange = ({ current, pageSize }) => {
  pagination.current = current
  pagination.size = pageSize
  pagination.pageSize = pageSize
  getProgramList()
}

const onPageChange = (current) => {
  pagination.current = current
  getProgramList()
}

const onSearch = () => {
  pagination.current = 1
  getProgramList()
}

const openCampaigns = (record) => campaignsModalRef.value?.show(record)
const openTierRates = (record) => tierRatesModalRef.value?.show(record)
const openPublishers = (record) => publishersModalRef.value?.show(record)

const isPrecheckPassed = (res) => {
  if (!res?.success) return false
  const data = res.data
  if (data === true) return true
  if (data?.canCreate === true) return true
  if (data?.passed === true) return true
  return false
}

const onAddProgram = async () => {
  try {
    const res = await accBonusProgramApi.getProgramPrecheck({
      marketplace: CURRENCY_MAPPER[countryCode].host,
    })
    if (!res.abtest) {
      router.push(
        `/seller/creatorConnections/accCreact?marketplace=${CURRENCY_MAPPER[countryCode].host}`,
      )
     
    }else{
      accAuthNoticeModalRef.value?.show()
    }
  } catch {
    // precheck 失败时展示提示
    
  }
}

const onEdit = (record) => {
  router.push(
    `/seller/creatorConnections/accCreact?marketplace=${CURRENCY_MAPPER[countryCode].host}&programId=${record.programId}`,
  )
}

const onView = (record) => {
  router.push(
    `/seller/creatorConnections/accBonusProgram/view/${record.programId}`,
  )
}

const onCancel = (record) => cancelModalRef.value?.show(record)
const statusType = (status) => {
  if (status === "ACTIVE") return "active"
  if (status === "COMPLETED") return "finished"
  if (status === "CANCELLED") return "inactive"
  if (status === "INCOMING") return "delivering"
  return "inactive"
}
onMounted(() => {
  getProgramList()
})
</script>

<style lang="less" scoped>
.acc-bonus-program {
  padding: 16px;
  padding-top: unset;
}

.table {
  padding: 0 16px;
  background-color: var(--color-white);
  border-radius: 8px;
  box-shadow: var(--section-box-shadow-6);
}

.table-head {
  display: flex;
  height: 64px;
  justify-content: space-between;
  align-items: center;

  .filters {
    display: flex;
    align-items: center;
    gap: 12px;
    background-color: #fff;
  }

  .statusFilter {
    display: flex;
    align-items: center;
    position: relative;
  }

  .filter-label {
    margin-right: 8px;
    font-family: Sora, sans-serif;
    font-size: 14px;
    color: var(--dark-2);
  }
}

.linkStyle {
  // color: #7653da;
  text-decoration: underline;
  margin-right: 12px;

  &:last-child {
    margin-right: 0;
  }
}

.action-links {
  white-space: nowrap;

  :deep(.artmisads-select-button + .artmisads-select-button) {
    margin-left: 8px;
  }
}

.sales-tip-icon {
  margin-left: 4px;
  width: 14px;
  height: 14px;
  vertical-align: -2px;
  cursor: pointer;
  color: #878394;
}
</style>

<style lang="less">
.acc-bonus-program-status-dropdown {
  z-index: 1100 !important;
}
</style>
